import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  VolumeUp as VolumeUpIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Assessment as AssessmentIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// 管理メニュー項目コンポーネント
const ManagementMenuItem = ({ icon, title, description, onClick }) => (
  <Card
    variant="outlined"
    sx={{
      mb: 2,
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
        transform: 'translateY(-1px)',
        boxShadow: 1,
      },
    }}
    onClick={onClick}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 3 }}>
      <Box sx={{ color: 'primary.main', mt: 0.5 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography 
          variant="subtitle1" 
          component="div" 
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          {description}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// コンテンツ作成メニュー項目コンポーネント
const ContentMenuItem = ({ icon, title, disabled, comingSoon, onClick }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      minHeight: 120,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.7 : 1,
      transition: 'all 0.2s ease-in-out',
      '&:hover': !disabled && {
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
        transform: 'translateY(-2px)',
        boxShadow: 2,
      },
    }}
    onClick={!disabled ? onClick : undefined}
  >
    <CardContent 
      sx={{ 
        textAlign: 'center', 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Box sx={{ color: 'primary.main', mb: 1.5 }}>{icon}</Box>
      <Typography 
        variant="subtitle2" 
        component="div"
        sx={{ 
          fontSize: '0.875rem',
          lineHeight: 1.2,
          fontWeight: 500
        }}
      >
        {title}
      </Typography>
      {comingSoon && (
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            mt: 0.5,
            fontSize: '0.75rem',
            fontStyle: 'italic'
          }}
        >
          Coming soon!
        </Typography>
      )}
    </CardContent>
  </Card>
);

const PostManagementDialog = ({ open, onClose }) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 3, pr: 6, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          投稿と管理
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* コンテンツ作成セクション */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            新規投稿
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={3}>
              <ContentMenuItem
                icon={<AssessmentIcon sx={{ fontSize: 32 }} />}
                title={t("common.model")}
                onClick={() => navigate("/upload/model")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <ContentMenuItem
                icon={<PeopleIcon sx={{ fontSize: 32 }} />}
                title={t("common.upload_character")}
                onClick={() => navigate("/upload/character")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <ContentMenuItem
                icon={<PhotoLibraryIcon sx={{ fontSize: 32 }} />}
                title={t("common.upload_image")}
                onClick={() => window.location.href = "/upload/image"}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <ContentMenuItem
                icon={<VideoLibraryIcon sx={{ fontSize: 32 }} />}
                title={t("common.upload_video")}
                onClick={() => navigate("/upload/video")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <ContentMenuItem
                icon={<DescriptionIcon sx={{ fontSize: 32 }} />}
                title={t("common.upload_text")}
                onClick={() => navigate("/upload/text")}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <ContentMenuItem
                icon={<EventIcon sx={{ fontSize: 32 }} />}
                title={t("common.add_event")}
                onClick={() => navigate("/upload/event")}
              />
            </Grid>
            <Grid 
              item 
              xs={6} 
              sm={4} 
              md={3} 
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ContentMenuItem
                icon={<VolumeUpIcon sx={{ fontSize: 32 }} />}
                title={t("common.upload_audio")}
                disabled
                comingSoon
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 管理セクション */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            管理
          </Typography>
          <ManagementMenuItem
            icon={<ManageAccountsIcon />}
            title={t("category.manage")}
            description="投稿作品の一覧確認、編集、削除などの管理を行います"
            onClick={() => navigate("/manage")}
          />
          <ManagementMenuItem
            icon={<AssessmentIcon />}
            title={t("common.analyze")}
            description="投稿作品のアクセス数、いいね数などの分析を行います"
            onClick={() => navigate("/analyze")}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* ノウハウセクション */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
            ノウハウ
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Link 
              to={"/text/bbc3563d-b982-4b6a-8d62-1909dc50f0d8"} 
              target='_blank'
              style={{ textDecoration: 'none' }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' },
                  py: 0.5
                }}
              >
                • 初めて自分でプロンプトを書く人のためのサンプルプロンプトガイド
              </Typography>
            </Link>
            <Link 
              to={"/image/f62d2486-5144-4cea-8904-8819205753ba"} 
              target='_blank'
              style={{ textDecoration: 'none' }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' },
                  py: 0.5
                }}
              >
                • 画像から生成(i2i)を使ってみよう
              </Typography>
            </Link>
            <Link 
              to={"/image/5a90a0cf-976e-4625-8c97-7b9e189fdc93"} 
              target='_blank'
              style={{ textDecoration: 'none' }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' },
                  py: 0.5
                }}
              >
                • ノウハウ_i2iのススメ
              </Typography>
            </Link>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export { PostManagementDialog };